/**
 * The Report Studio's left pane — `Evidence — All Tournament` — and the Report Readiness ring.
 *
 * Authority: `um-app/06-report-studio.md` §3 (the whole pane), §3.1 (the ring), §3.2 (the
 * count line), §3.3 (the criteria groups and the `🔒 Yours Only` chip), §3.4 (the footer) and
 * §4.5 (the thin-criterion advisory); `um-app/04-umpire-timeline.md` §2.2 (the ring's
 * semantics) and §2.4 (the author chip and the provenance marker).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR RULES THIS COMPONENT EXISTS TO KEEP
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **IT INVENTS NOTHING.** Every figure on it — the ring, the three counts, each group's
 *     count, the thin list — arrives computed from the API, which computed it from the notes
 *     themselves. Nothing is estimated, defaulted or filled in while a fetch is in flight: an
 *     evidence pane drawn from nothing would be a claim about what the tournament recorded.
 *
 *  2. **THE RING IS NOT A SCORE, AND NOTHING HERE LABELS IT AS ONE** (spec 04 §6, Q-04b). It
 *     reads `Report Readiness`, verbatim, and its tooltip and screen-reader label both say
 *     what it counts: criteria with released evidence, out of the criteria set.
 *
 *  3. **EVERY ROW IS LABELLED `Draft` · `Locked` · `Released`, VERBATIM** (§H, ruling §I).
 *     Under §I the pane holds the reader's own Drafts as well as her Locked notes, so
 *     "hers alone" is no longer one thing: a Draft is still releasable, a Locked note is hers
 *     by a deliberate press, and a reader who confused the two would misjudge what the panel
 *     can see. The label is the row's own `visibility`, never inferred from `mine`.
 *
 *     Shared and hers-alone are separated in **three** channels, not one — the label, the
 *     dashed rule down the side, and the words in the tooltip — because colour alone is never
 *     the only channel in this sheet, and this is the distinction that must never be misread.
 *     Both are the reader's own by construction: no other author's unreleased note is in the
 *     response at all.
 *
 *  4. **THE COUNT LINE IS NOT A PARTITION, AND THE COPY DOES NOT PRETEND IT IS** (§3.2). The
 *     three figures are the total pool, what the team contributed, and everything of the
 *     reader's the panel has never seen; they do not sum, and the pane explains that rather
 *     than hiding it. The Draft/Locked split beneath the third figure comes from the API too —
 *     the pane counts no rows of its own, so no figure on it can drift from the one above it.
 *
 *     **The ring, each group's count and the thin list are RELEASED evidence only** (§I,
 *     unchanged). A Draft or a Locked note is in the group and not in its count, and the group
 *     header says so rather than leaving the reader to notice the discrepancy.
 *
 * The grouping selector of §3 (`Grouped By Criteria ▾`, with chronological and by-match
 * alternatives) is **absent rather than stubbed**: the API sends one grouping and a dropdown
 * offering three that all render the same list would be a control that lies about what it does.
 */

import { Link } from "react-router-dom";

import { formatReadiness, readinessFraction, tidyCriterionDisplayName } from "@hockeytd/shared";

import type { EvidenceGroup, EvidenceNote, UmEvidenceBody } from "../../api/types.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";

/**
 * §H's status labels, verbatim and capitalised — `Draft · Locked · Released`. One spelling,
 * here, so no surface can invent a fourth word for a state the system has three of.
 *
 * Exported because the consolidated feedback workbook (spec 11 §5) prints the same three
 * words in its `Status` column: a spreadsheet that said `Private` where the pane says `Locked`
 * would be a second vocabulary for one set of states.
 */
export const VISIBILITY_LABEL = {
  draft: "Draft",
  locked: "Locked",
  released: "Released",
} as const;

/**
 * What each label promises the reader, in words. Never colour alone.
 *
 * **RULING R4″ (Alfred, the Technical Delegate, 2026-09-09) changed the second half of the two
 * private promises and they are re-worded here rather than left to read as more than they are.**
 * The WORDS of a draft or a locked note are still nobody's but the author's — that has not moved
 * an inch. What the TD can now see is a per-manager count of recorded, pending and released
 * notes and the time of the last capture, so "he has not seen it" is said precisely and the
 * counting is said plainly beside it.
 */
const VISIBILITY_TITLE = {
  draft:
    "Yours alone to read. Captured but never released, so no other Umpires Manager and not " +
    "the Technical Delegate has read a word of it — he can see that you hold unreleased " +
    "notes and how many, never what they say. You can still release it, on any later day.",
  locked:
    "Yours alone to read, permanently. You locked it, so no peer and not the Technical " +
    "Delegate has read it — it counts towards the pending total he can see, and nothing " +
    "tells him a locked note apart from a draft. It still writes your report.",
  released: "Shared with the panel and the Technical Delegate when you released it.",
} as const;

/** The ring's geometry. A donut, drawn once, in the one place its numbers are known. */
const RING = { size: 56, stroke: 6 } as const;

/**
 * The Report Readiness ring (§3.1, spec 04 §2.2) — an SVG donut, accent arc on a hairline
 * track, with a centred mono value and the label beneath.
 *
 * `readinessFraction` and `formatReadiness` come from `@hockeytd/shared`, so the arc, the
 * figure and the API's own arithmetic cannot disagree.
 */
export function ReadinessRing({
  covered,
  total,
}: {
  covered: number;
  total: number;
}): React.JSX.Element {
  const radius = (RING.size - RING.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * readinessFraction({ covered, total });
  const description =
    `Report Readiness: ${String(covered)} of ${String(total)} criteria have at least one ` +
    "released note. It measures the evidence base for the report, not the umpire's performance.";

  return (
    <div className="ring" data-testid="readiness-ring" data-howto="readiness-ring">
      <svg
        width={RING.size}
        height={RING.size}
        viewBox={`0 0 ${String(RING.size)} ${String(RING.size)}`}
        role="img"
        aria-label={description}
      >
        <title>{description}</title>
        <circle
          className="ring__track"
          cx={RING.size / 2}
          cy={RING.size / 2}
          r={radius}
          fill="none"
          strokeWidth={RING.stroke}
        />
        <circle
          className="ring__arc"
          cx={RING.size / 2}
          cy={RING.size / 2}
          r={radius}
          fill="none"
          strokeWidth={RING.stroke}
          strokeLinecap="round"
          strokeDasharray={`${String(filled)} ${String(circumference)}`}
          // Twelve o'clock, clockwise — a ring that started at three would read as a gauge.
          transform={`rotate(-90 ${String(RING.size / 2)} ${String(RING.size / 2)})`}
        />
      </svg>
      <span className="ring__val num" data-testid="readiness-value">
        {formatReadiness({ covered, total })}
      </span>
      <span className="ring__label">Report Readiness</span>
    </div>
  );
}

/** `M07 · Q1 · 08′` — the mono provenance marker, linking back to the match (gap J2). */
export function ProvenanceMarker({ note }: { note: EvidenceNote }): React.JSX.Element {
  const parts = [
    note.matchNo === null ? null : note.matchNo,
    note.quarter === null ? null : `Q${String(note.quarter)}`,
    note.matchMinute === null
      ? null
      : `${String(note.matchMinute)}′${note.minuteProvisional ? "?" : ""}`,
  ].filter((part): part is string => part !== null);

  const label = parts.length === 0 ? "No Match Stamp" : parts.join(" · ");
  // A marker with no match behind it is text, not a dead link.
  if (note.matchId === null) {
    return <span className="tnote__m num">{label}</span>;
  }
  return (
    <Link
      className="tnote__m num"
      to={`${UM_ROOT_PATH}/match/${String(note.matchId)}`}
      title={
        note.minuteProvisional
          ? "The minute is provisional — the clock was estimated when this note was taken."
          : "Open the match this note was taken in."
      }
    >
      {label}
    </Link>
  );
}

function EvidenceRow({ note }: { note: EvidenceNote }): React.JSX.Element {
  return (
    <li
      className={`tnote${note.yoursOnly ? " tnote--mine" : ""}`}
      data-testid={`note-${note.id}`}
    >
      <span className="tnote__chips">
        {/* Attribution first, as spec 04 §2.4 draws it — `You` for the reader's own. */}
        <span className="tnote__au">
          {note.mine ? "You" : `UM · ${note.authorShortName ?? "—"}`}
        </span>
        {/* Then the §H status label, verbatim, on every row without exception. */}
        <span
          className={`tnote__vis tnote__vis--${note.visibility}`}
          data-testid={`visibility-${note.id}`}
          title={VISIBILITY_TITLE[note.visibility]}
        >
          {note.visibility === "locked" ? "🔒 " : ""}
          {VISIBILITY_LABEL[note.visibility]}
        </span>
      </span>
      <span className="tnote__tx">
        {note.bodyEn}
        <ProvenanceMarker note={note} />
      </span>
    </li>
  );
}

function CriterionGroup({ group }: { group: EvidenceGroup }): React.JSX.Element {
  const yoursOnly = group.notes.filter((note) => note.yoursOnly).length;
  return (
    <section
      className="catgrp"
      data-testid={`catgrp-${group.criterionStableKey ?? "ungrouped"}`}
    >
      <h4 className="catgrp__h">
        <span className="catgrp__nm">{tidyCriterionDisplayName(group.name)}</span>
        <span className="catgrp__n num">
          {group.releasedCount === 1 ? "1 Note" : `${String(group.releasedCount)} Notes`}
          {/* The group can hold more rows than it counts: the count is the RELEASED evidence
              the thin-criterion rule weighs, and neither a Draft nor a Locked note is shared
              evidence (§I — the ring's meaning is preserved). */}
          {yoursOnly === 0 ? "" : ` + ${String(yoursOnly)} Yours Only`}
        </span>
      </h4>

      {group.notes.length === 0 ? (
        <p className="catgrp__empty">
          No evidence yet. The group is shown because a criterion with nothing behind it is the
          gap worth seeing.
        </p>
      ) : (
        <ul className="tnotes">
          {group.notes.map((note) => (
            <EvidenceRow key={note.id} note={note} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function UmEvidencePane({ evidence }: { evidence: UmEvidenceBody }): React.JSX.Element {
  const { counts, readiness } = evidence;
  const plural = (n: number, one: string, many: string): string =>
    `${String(n)} ${n === 1 ? one : many}`;

  return (
    <section
      className="panel panel--d1 umevidence"
      aria-labelledby="umEvidenceTitle"
      data-howto="evidence-pane"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="umEvidenceTitle">
          Evidence — All Tournament
        </h3>
        <span className="panel__note">Grouped By Criteria</span>
        <span className="spacer" />
        <ReadinessRing covered={readiness.covered} total={readiness.total} />
      </div>

      <p className="countline num" data-testid="evidence-countline">
        {plural(counts.total, "Note", "Notes")} ·{" "}
        {plural(counts.sharedByTheTeam, "Shared By The Team", "Shared By The Team")} ·{" "}
        {plural(counts.yoursOnly, "Yours Only", "Yours Only")}
      </p>
      {counts.yoursOnly === 0 ? null : (
        <p className="countline__split" data-testid="evidence-yoursonly-split">
          Yours only: {plural(counts.yoursOnlyDrafts, "Draft", "Drafts")} ·{" "}
          {plural(counts.yoursOnlyLocked, "Locked", "Locked")}
        </p>
      )}
      <p className="umnote">
        The three figures are not a division of one another: the first is the whole pool for
        this umpire across both competitions, the second is what the other managers
        contributed, and the third is everything of yours the panel has never seen — your
        Drafts and your Locked notes. Writing the report is the one place you see all of it;
        a Draft is still yours to release on any later day, and neither kind moves the
        readiness ring, which measures only what the rest of the panel can also see.
      </p>

      {counts.total === 0 ? (
        <p className="catgrp__empty" data-testid="evidence-empty">
          <b>No Released Notes Yet For This Umpire.</b> Nothing has been shared with the team
          about them, so there is nothing here to draft from yet.
        </p>
      ) : null}

      {evidence.thinCriteria.length === 0 ? null : (
        <div className="gapstrip" data-testid="evidence-thin">
          <b>Thin On Evidence</b>
          <div>
            {evidence.thinCriteria.map((criterion) => tidyCriterionDisplayName(criterion.name)).join(" · ")} —{" "}
            {evidence.thinCriteria.length === 1
              ? "one criterion rests on fewer than two released observations."
              : `${String(evidence.thinCriteria.length)} criteria rest on fewer than two released observations.`}
          </div>
          <div>
            Advisory only. Nothing here blocks the draft or the push — it is what makes the
            omission visible, not a gate.
          </div>
        </div>
      )}

      <div className="catgrps">
        {evidence.groups.map((group) => (
          <CriterionGroup key={group.criterionStableKey ?? "ungrouped"} group={group} />
        ))}
      </div>

      <div className="panel__foot">
        <b>Attributed Inside The Team · Stripped In The Draft —</b> the report reads as the
        panel&rsquo;s assessment, never as &ldquo;X said, Y said&rdquo;. Whole tournament, both
        competitions: one pool of notes, one report.
      </div>
    </section>
  );
}
